import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import { api, resolveAssetUrl } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { Loader2, ChevronRight } from "lucide-react";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  position: "Guard",
  height: "",
  years_experience: "",
  resume_url: "",
  film_url: "",
  testimony: "",
};

const POSITIONS = [
  "Point Guard",
  "Shooting Guard",
  "Small Forward",
  "Power Forward",
  "Center",
  "Guard",
  "Forward",
];

const LadyStarzz = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.resume_url || !form.film_url || !form.testimony) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/tryouts", form);
      toast.success(res.data?.message || "Application received. Watch your inbox.");
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-x-hidden bg-[#0b0b0b] text-white" data-testid="lady-starzz-page">
      <Header />
      <CartDrawer />

      {/* HERO */}
      <section
        className="relative min-h-[90vh] flex items-end overflow-hidden"
        data-testid="starzz-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${resolveAssetUrl(BRAND.LADY_STARZZ_TEAM)})` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(11,11,11,0.55) 0%, rgba(11,11,11,0.05) 30%, rgba(11,11,11,0.25) 60%, rgba(11,11,11,0.95) 100%)",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-32 w-full">
          <div className="inline-flex items-center gap-3 mb-6 bg-black/40 backdrop-blur-md border border-white/10 px-4 py-2">
            <img
              src={resolveAssetUrl(BRAND.WSBL_LOGO)}
              alt="WSBL"
              className="h-8 w-8 object-contain"
              data-testid="starzz-wsbl-badge"
            />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 font-bold">
              Women's Southwest Basketball League · 2027 Inaugural Season
            </span>
          </div>
          <h1
            className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tight leading-[0.95] max-w-5xl"
            data-testid="starzz-hero-title"
          >
            Texas Lady Starzz
          </h1>
          <p className="text-xl sm:text-2xl font-heading tracking-widest uppercase metallic-text mt-3">
            Hoops in Global Unity
          </p>
          <p className="mt-6 text-zinc-200 text-base leading-relaxed max-w-2xl drop-shadow-lg">
            Houston-based · Pro-ready talent · Faith first. The flagship women's basketball franchise of Faith Athletic Clothing — built to compete on the floor and carry the gospel beyond it.
          </p>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 bg-white text-black font-heading font-bold text-sm tracking-[0.25em] uppercase mt-8 px-8 py-4 hover:bg-zinc-200 transition-colors"
            data-testid="starzz-apply-cta"
          >
            Apply for Tryout <ChevronRight size={16} />
          </a>
        </div>
      </section>

      {/* TEAM LOGO BADGE */}
      <section className="bg-gradient-to-b from-black via-zinc-950 to-black border-y border-zinc-900 py-16" data-testid="starzz-logo-section">
        <div className="max-w-5xl mx-auto px-6 flex flex-col items-center text-center">
          <img
            src={resolveAssetUrl(BRAND.LADY_STARZZ_LOGO)}
            alt="Texas Lady Starzz — Women's Basketball"
            className="w-64 sm:w-80 lg:w-96 h-auto object-contain drop-shadow-[0_0_30px_rgba(139,92,246,0.35)]"
            data-testid="starzz-team-logo"
          />
          <div className="text-[10px] tracking-[0.5em] uppercase text-zinc-500 mt-8">
            // Official Team Mark
          </div>
          <p className="font-heading text-2xl sm:text-3xl uppercase font-black tracking-tight mt-3 metallic-text">
            Texas Lady Starzz
          </p>
          <p className="text-zinc-400 text-xs tracking-[0.35em] uppercase mt-2">
            Est. @2027 · Houston, TX
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="starzz-mission">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">
              // Our Mission
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase leading-tight mt-3">
              More Than a Roster.
              <br />
              <span className="metallic-text">A Movement.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-zinc-300 text-base leading-relaxed">
            <p>
              The <b className="text-white">Texas Lady Starzz</b> begin play in the 2027 inaugural season of the Women's Southwest Basketball League as a Houston-based franchise. Our mission is two-fold: develop pro-ready talent at the highest competitive level, and equip every athlete on this roster to <b className="text-white">share her faith and testimony</b> — locally in the community and nationwide through the platform of the league.
            </p>
            <p>
              We're recruiting women who still have a fire for the game, who play with discipline, and whose walk with God has shaped who they are on and off the court. If that's you, we want your film.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="border-l-2 border-white/20 pl-4">
                <div className="font-heading text-3xl font-black">2027</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">Inaugural Season</div>
              </div>
              <div className="border-l-2 border-white/20 pl-4">
                <div className="font-heading text-3xl font-black">HOU</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">Home Base</div>
              </div>
              <div className="border-l-2 border-white/20 pl-4">
                <div className="font-heading text-3xl font-black">WSBL</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">Pro League</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WSBL Strip */}
      <section className="bg-gradient-to-br from-zinc-950 via-black to-zinc-950 border-y border-zinc-900 py-16" data-testid="starzz-wsbl-strip">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center gap-10">
          <img
            src={resolveAssetUrl(BRAND.WSBL_LOGO)}
            alt="Women's Southwest Basketball League"
            className="w-40 h-40 object-contain flex-shrink-0"
          />
          <div className="text-center sm:text-left">
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-2">// League Partner</div>
            <h3 className="font-heading text-3xl sm:text-4xl font-black uppercase leading-tight">
              Women's Southwest
              <br />
              Basketball League
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mt-4 max-w-xl">
              A regional professional women's basketball league launching across the southwest. The Lady Starzz are honored to compete as a founding franchise representing Houston, Texas — and the Faith Athletic banner.
            </p>
          </div>
        </div>
      </section>

      {/* APPLY FORM */}
      <section id="apply" className="max-w-3xl mx-auto px-6 py-24" data-testid="starzz-apply-section">
        <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">// Player Submission</div>
        <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase leading-tight mt-3">
          Apply for Tryout
        </h2>
        <p className="text-zinc-400 text-sm leading-relaxed mt-4 max-w-2xl">
          Send us your player resume, game film, and a short testimony of how your faith in God's plan has shaped your life. Our staff reviews every submission. Strong applicants will be contacted within 7–14 days.
        </p>

        {submitted ? (
          <div className="mt-10 border border-white/15 bg-white/5 p-8 text-center" data-testid="starzz-submitted">
            <h3 className="font-heading text-3xl uppercase font-black metallic-text">Application Received</h3>
            <p className="text-zinc-300 text-sm mt-3 max-w-lg mx-auto">
              Thank you for stepping forward. Check your inbox for a confirmation. We'll be in touch.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white mt-6 underline"
              data-testid="starzz-submit-another"
            >
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="starzz-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name *" id="name">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                  data-testid="starzz-input-name"
                />
              </Field>
              <Field label="Email *" id="email">
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  data-testid="starzz-input-email"
                />
              </Field>
              <Field label="Phone *" id="phone">
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  data-testid="starzz-input-phone"
                />
              </Field>
              <Field label="Position *" id="position">
                <select
                  value={form.position}
                  onChange={update("position")}
                  className={inputCls}
                  data-testid="starzz-input-position"
                >
                  {POSITIONS.map((p) => (
                    <option key={p} value={p} className="bg-black">
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Height (e.g. 5'10&quot;)" id="height">
                <input
                  type="text"
                  value={form.height}
                  onChange={update("height")}
                  placeholder='5&apos;10"'
                  className={inputCls}
                  data-testid="starzz-input-height"
                />
              </Field>
              <Field label="Years of Experience" id="exp">
                <input
                  type="text"
                  value={form.years_experience}
                  onChange={update("years_experience")}
                  placeholder="College / Pro / Overseas"
                  className={inputCls}
                  data-testid="starzz-input-experience"
                />
              </Field>
            </div>

            <Field label="Player Resume URL * (Google Drive / Dropbox link)" id="resume">
              <input
                type="url"
                required
                value={form.resume_url}
                onChange={update("resume_url")}
                placeholder="https://"
                className={inputCls}
                data-testid="starzz-input-resume"
              />
            </Field>

            <Field label="Game Film URL * (Hudl / YouTube / Vimeo)" id="film">
              <input
                type="url"
                required
                value={form.film_url}
                onChange={update("film_url")}
                placeholder="https://"
                className={inputCls}
                data-testid="starzz-input-film"
              />
            </Field>

            <Field label="Faith Testimony * (How has your faith shaped your story?)" id="testimony">
              <textarea
                required
                rows={5}
                value={form.testimony}
                onChange={update("testimony")}
                className={`${inputCls} resize-none`}
                data-testid="starzz-input-testimony"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-white text-black font-heading font-bold text-base tracking-[0.25em] uppercase py-4 hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="starzz-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Submitting…
                </>
              ) : (
                "Submit Application"
              )}
            </button>
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 text-center">
              By submitting you confirm the information is accurate.
            </p>
          </form>
        )}

        <div className="mt-16 text-center">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white"
            data-testid="starzz-back-home"
          >
            ← Back to Faith Athletic
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const inputCls =
  "w-full bg-transparent border border-zinc-800 focus:border-white text-white px-4 py-3 text-sm tracking-wide outline-none transition-colors placeholder:text-zinc-700";

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default LadyStarzz;
